import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/use-toast';
import { CalendarClock, Check, Loader2, Mail, MapPin, MessageCircle, MonitorPlay, Phone, Users, X } from 'lucide-react';
import CalendarFeedLink from '@/components/admin/recruitment/CalendarFeedLink';
import { bookingTitle, dayLabel, dueSoon, groupByDay, pillState, timeLabel } from '@/lib/bookings';

/**
 * The Bookings pill in the admin header, and the schedule it opens.
 *
 * One list of what is booked with the team — Meet & Greets at an outbase and
 * phone call-backs the WhatsApp assistant booked — for the next fortnight,
 * plus anything from the last fortnight nobody marked done. The pill shows
 * how many are today, turns amber when one starts within the hour, and
 * carries a red dot while something is overdue. Done and Cancel go through
 * admin_booking_complete / admin_booking_cancel, which also free the slot and
 * close the call-back request, so the slot manager and this list never
 * disagree. Nobody is messaged by a cancel — the admin tells them from the
 * inbox — with ONE exception (3 Oct 2026): a B2B WhatsApp call booked by the
 * booking agent cancels through its 'cancel' phase instead, which tells the
 * prospect instantly by email and WhatsApp (new times to pick while their
 * 24h window is open, the approved template after).
 */

// A B2B agent booking is the only callback whose callback_status carries the
// MEETING's state; a call-back request's own statuses are open/booked/done/
// dismissed, so these two values identify the second rail unambiguously.
const isB2bCall = (row) => row.kind === 'callback' && ['confirmed', 'requested'].includes(row.callback_status || '');

const INBOX = '/admin/dashboard/recruitment?tab=rescue&section=inbox';
const PIPELINE = '/admin/dashboard/recruitment?tab=pipeline';

// headless / openRef / onCount: see WhatsAppHeaderSheet — the Smart Dash frame
// hosts this desk without its pill. With no props it renders as before.
export default function BookingsHeaderPill({ headless = false, openRef = null, onCount = null } = {}) {
    const { toast } = useToast();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    useEffect(() => { if (openRef) openRef.current = () => setOpen(true); }, [openRef]);
    const [schedule, setSchedule] = useState(null);
    const [now, setNow] = useState(Date.now());
    const [busy, setBusy] = useState(null);

    // Demo asks with no slot yet — a preferred time for the team to confirm
    // (11 Oct 2026). Booked demos are appointments and come in the schedule.
    const [demoAsks, setDemoAsks] = useState([]);

    const load = useCallback(async () => {
        const { data, error } = await supabase.rpc('admin_bookings_schedule', { p_days: 14 });
        if (!error && data) setSchedule(data);
        const { data: asks } = await supabase.from('demo_requests')
            .select('id, created_at, name, company, email, phone, mode, part_key, preferred_at, note, channel')
            .eq('status', 'open').order('preferred_at', { ascending: true }).limit(50);
        setDemoAsks(asks || []);
    }, []);

    const closeAsk = async (ask, status) => {
        if (status === 'dismissed' && !window.confirm(`Dismiss the demo request from ${ask.name}? They will not be told automatically.`)) return;
        setBusy(ask.id);
        const { error } = await supabase.from('demo_requests')
            .update({ status, handled_at: new Date().toISOString() }).eq('id', ask.id).eq('status', 'open');
        if (!error) await supabase.from('admin_alerts').update({ resolved_at: new Date().toISOString() })
            .eq('dedup_key', `demo:${ask.id}`).is('resolved_at', null);
        setBusy(null);
        if (error) toast({ title: 'Could not update the request', description: error.message, variant: 'destructive' });
        else { toast({ title: status === 'done' ? 'Marked handled' : 'Dismissed' }); load(); }
    };

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const ch = supabase.channel('bookings-header')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'candidate_appointments' }, () => load())
            // The booking agent's rail (published by 20261259): a B2B booking,
            // cancel or instant rebook refreshes the pill the moment it lands.
            .on('postgres_changes', { event: '*', schema: 'public', table: 'b2b_meetings' }, () => load())
            .subscribe();
        const t = setInterval(() => { setNow(Date.now()); load(); }, 60000);
        return () => { clearInterval(t); supabase.removeChannel(ch).catch(() => {}); };
    }, [load]);

    const state = pillState(schedule, now);
    useEffect(() => { onCount?.({ today: state.today || 0, overdue: state.overdue || 0, next: state.next || null }); }, [onCount, state.today, state.overdue, state.next]);

    const act = async (row, action) => {
        const b2b = isB2bCall(row);
        if (action === 'cancel' && !window.confirm(b2b
            ? `Cancel ${bookingTitle(row)}? They'll be told straight away by email and WhatsApp, with new times to pick.`
            : `Cancel ${bookingTitle(row)}? They will not be told automatically — tell them from the inbox.`)) return;
        setBusy(row.id);
        try {
            if (action === 'cancel' && b2b) {
                const { data, error } = await supabase.functions.invoke('b2b-whatsapp-booking', { body: { phase: 'cancel', meeting_id: row.id } });
                if (error) throw error;
                if (!data?.ok) throw new Error(data?.error || 'Refused.');
                toast({ title: 'Cancelled', description: data.message });
            } else {
                const { data, error } = await supabase.rpc(action === 'done' ? 'admin_booking_complete' : 'admin_booking_cancel', { p_appointment_id: row.id });
                if (error) throw error;
                if (!data?.success) throw new Error(data?.error || 'Refused.');
                toast({ title: action === 'done' ? 'Marked done' : 'Cancelled', description: action === 'cancel' && data.slot_freed ? 'The slot is open again.' : undefined });
            }
            await load();
        } catch (e) {
            toast({ title: 'Could not update the booking', description: e.message, variant: 'destructive' });
        } finally {
            setBusy(null);
        }
    };

    const go = (path) => { setOpen(false); navigate(path); };

    const Row = ({ row, overdue = false }) => {
        const soon = !overdue && dueSoon(row.start_time, now);
        const isCall = row.kind === 'callback';
        const isDemo = row.kind === 'demo';
        return (
            <div className={`rounded-2xl border px-3.5 py-3 ${overdue ? 'border-amber-200 bg-amber-50/60' : soon ? 'border-emerald-200 bg-emerald-50/50' : 'border-border bg-card'}`}>
                <div className="flex items-start gap-3">
                    <div className="w-14 shrink-0 pt-0.5 text-[13px] font-black tabular-nums text-foreground">{timeLabel(row.start_time)}</div>
                    <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[13px] font-bold text-foreground">{bookingTitle(row)}</span>
                            <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-black uppercase tracking-widest ${isDemo ? 'bg-emerald-100 text-emerald-700' : isCall ? 'bg-indigo-100 text-indigo-700' : 'bg-blue-100 text-blue-700'}`}>
                                {isDemo ? <MonitorPlay size={9} /> : isCall ? <Phone size={9} /> : <MapPin size={9} />} {isDemo ? (/·\s*in person/.test(row.notes || '') ? 'Demo · in person' : 'Demo · video') : isCall ? (row.callback_channel === 'sms' ? 'SMS' : 'WhatsApp') : 'Outbase'}
                            </span>
                            {overdue && <span className="rounded-full bg-amber-200 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-amber-800">not marked done</span>}
                            {soon && <span className="rounded-full bg-emerald-200 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-emerald-800">within the hour</span>}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                            {row.phone && <a href={`tel:${row.phone}`} className="font-semibold text-foreground hover:underline">{row.phone}</a>}
                            {row.admin_name && <span>with {row.admin_name}</span>}
                            {row.candidate_status && <span>applicant · {String(row.candidate_status).replaceAll('_', ' ')}</span>}
                            {isCall && !row.candidate_id && <span>not an applicant yet</span>}
                        </div>
                        {(row.callback_note || row.notes) && (
                            <p className="mt-1 line-clamp-2 text-[11px] text-muted-foreground">{row.callback_note || row.notes}</p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-1.5">
                            {(isCall || (row.candidate_id && !isDemo)) && (
                                <button type="button" onClick={() => go(`${INBOX}&thread=${encodeURIComponent(row.thread_key)}`)}
                                    className="inline-flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:bg-accent/40">
                                    <MessageCircle size={11} /> Thread
                                </button>
                            )}
                            {row.candidate_id && (
                                <button type="button" onClick={() => go(PIPELINE)}
                                    className="inline-flex items-center gap-1 rounded-lg border border-border px-2 py-1 text-[10px] font-black uppercase tracking-widest text-muted-foreground hover:bg-accent/40">
                                    <Users size={11} /> Pipeline
                                </button>
                            )}
                            <button type="button" disabled={busy === row.id} onClick={() => act(row, 'done')}
                                className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-700 hover:bg-emerald-100 disabled:opacity-60">
                                {busy === row.id ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />} Done
                            </button>
                            <button type="button" disabled={busy === row.id} onClick={() => act(row, 'cancel')}
                                className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-rose-600 hover:bg-rose-50 disabled:opacity-60">
                                <X size={11} /> Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const days = groupByDay(schedule?.upcoming || [], now);

    return (
        <>
            {!headless && <button
                type="button"
                onClick={() => { setOpen(true); load(); }}
                title={state.next ? `Next: ${bookingTitle(state.next)} at ${timeLabel(state.next.start_time)}` : 'Bookings'}
                className={`relative flex items-center gap-1.5 px-3 h-7 rounded-lg border text-[11px] font-semibold transition-colors ${
                    state.soon
                        ? 'border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200'
                        : state.today
                            ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 dark:border-blue-800/50 dark:bg-blue-900/20 dark:text-blue-300'
                            : 'border-border bg-card text-muted-foreground hover:bg-accent/40'}`}
            >
                <CalendarClock className="h-3 w-3" />
                <span className="hidden lg:inline">Bookings</span>
                {state.today > 0 && (
                    <span className={`ml-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold text-white ${state.soon ? 'bg-amber-500' : 'bg-blue-600'}`}>{state.today}</span>
                )}
                {state.overdue > 0 && <span title={`${state.overdue} not marked done`} className="absolute -right-1 -top-1 h-2 w-2 rounded-full bg-rose-500" />}
            </button>}

            <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (v) load(); }}>
                <SheetContent side="right" className="w-full overflow-y-auto p-0 sm:max-w-xl">
                    <SheetHeader className="border-b border-border px-5 py-4">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <SheetTitle className="flex items-center gap-2 text-base"><CalendarClock size={18} className="text-blue-600" /> Bookings</SheetTitle>
                                <SheetDescription className="text-xs">Meet &amp; Greets, phone call-backs and product demos for the next fortnight. Mark them done here; a cancel frees the slot and you tell the person from the inbox — except a B2B WhatsApp call, where they're emailed and messaged new times automatically.</SheetDescription>
                            </div>
                            <div className="shrink-0"><CalendarFeedLink /></div>
                        </div>
                    </SheetHeader>
                    <div className="space-y-5 p-4 sm:p-5">
                        {schedule === null && <div className="flex justify-center py-10"><Loader2 className="animate-spin text-slate-400" /></div>}
                        {schedule && (schedule.overdue || []).length > 0 && (
                            <section className="space-y-2">
                                <h3 className="text-[10px] font-black uppercase tracking-widest text-amber-700">Not marked done</h3>
                                {schedule.overdue.map((row) => <Row key={row.id} row={row} overdue />)}
                            </section>
                        )}
                        {demoAsks.length > 0 && (
                            <section className="space-y-2">
                                <h3 className="text-[10px] font-black uppercase tracking-widest text-emerald-700">Demo requests — confirm a time</h3>
                                {demoAsks.map((ask) => (
                                    <div key={ask.id} className="rounded-2xl border border-emerald-200 bg-emerald-50/40 px-3.5 py-3">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <span className="text-[13px] font-bold text-foreground">Demo: {[ask.name, ask.company].filter(Boolean).join(', ')}</span>
                                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[9px] font-black uppercase tracking-widest text-emerald-700">
                                                <MonitorPlay size={9} /> {ask.mode === 'in_person' ? 'in person' : 'video'}{ask.channel !== 'web' ? ` · ${ask.channel}` : ''}
                                            </span>
                                        </div>
                                        <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                                            {ask.preferred_at && <span className="font-semibold text-foreground">prefers {dayLabel(ask.preferred_at, now)} {timeLabel(ask.preferred_at)}</span>}
                                            {ask.part_key && <span>{ask.part_key}</span>}
                                            {ask.phone && <a href={`tel:${ask.phone}`} className="font-semibold text-foreground hover:underline">{ask.phone}</a>}
                                            {ask.email && <a href={`mailto:${ask.email}`} className="inline-flex items-center gap-1 hover:underline"><Mail size={10} /> {ask.email}</a>}
                                        </div>
                                        {ask.note && <p className="mt-1 line-clamp-2 text-[11px] text-muted-foreground">{ask.note}</p>}
                                        <div className="mt-2 flex flex-wrap gap-1.5">
                                            <button type="button" disabled={busy === ask.id} onClick={() => closeAsk(ask, 'done')}
                                                className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-700 hover:bg-emerald-100 disabled:opacity-60">
                                                <Check size={11} /> Handled
                                            </button>
                                            <button type="button" disabled={busy === ask.id} onClick={() => closeAsk(ask, 'dismissed')}
                                                className="inline-flex items-center gap-1 rounded-lg border border-rose-200 px-2 py-1 text-[10px] font-black uppercase tracking-widest text-rose-600 hover:bg-rose-50 disabled:opacity-60">
                                                <X size={11} /> Dismiss
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </section>
                        )}
                        {schedule && days.length === 0 && (
                            <p className="py-8 text-center text-xs font-bold text-muted-foreground">Nothing booked for the next fortnight. Publish availability in the Meet &amp; Greet Slot Manager; the assistant offers phone call-back blocks by itself.</p>
                        )}
                        {days.map((day) => (
                            <section key={day.key} className="space-y-2">
                                <h3 className="text-[10px] font-black uppercase tracking-widest text-muted-foreground">{day.label}</h3>
                                {day.rows.map((row) => <Row key={row.id} row={row} />)}
                            </section>
                        ))}
                    </div>
                </SheetContent>
            </Sheet>
        </>
    );
}
